import { PIECES, isAir, isArmored, isBuilding, isCombatAircraft, isTransport } from "../engine/pieces";
import { directedNeighbor, distance, inside, key, neighbors, parseKey } from "../engine/hex";
import { rollD6 } from "../engine/state";
import {
  areEnemies, attack, attackBridge, beginProduction, board, canAttack, canAttackBridge,
  canBoard, canCapture, canCaptureCivil, canHarvest, canOrbitalStrike, canReload, canRepairBridge, canSuppress,
  canRetaliate, canUnload, canUnboard, capture, captureCivil, categoryForTarget,
  checkVictory, configureOutput, confirmMovement, deploy, endTurn, energy,
  finishActivation, harvesterAction, isDrone, move, orbitalStrike, order, piecesAt,
  reachable, recycle, refineryOutput, reload, repairBridge, sell, suppress, transform, unboard,
  validBuildingOutput, validDeployment,
} from "../engine/rules";
import type { Category, Faction, GamePiece, GameState, PieceType } from "../engine/types";

type Cell = [number, number];
type AiProfile = "RUSH" | "BALANCED" | "ECONOMY";
type RetaliationDecision = (attacker: GamePiece, target: GamePiece) => boolean | null;

function attackWithDecision(s:GameState,attacker:GamePiece,target:GamePiece,retaliationDecision:RetaliationDecision):boolean{
  if(canRetaliate(s,target,attacker)){
    const decision=retaliationDecision(attacker,target);
    if(decision===null){s.pendingRetaliation={attackerId:attacker.id,targetId:target.id,attackerFaction:attacker.owner,targetFaction:target.owner};s.event=`${attacker.type} attacks ${target.type}. Retaliation decision required.`;return false;}
    return attack(s,attacker,target,decision).success;
  }
  return attack(s,attacker,target,true).success;
}
export interface AiAnalysis {
  profile: AiProfile;
  mapScale: "SMALL" | "MEDIUM" | "LARGE";
  oreRemaining: number;
  nearestThreat: number;
  enemyComposition: Record<Category, number>;
}
export interface AiDeploymentChoice { col: number; row: number; facing?: number }

const categories: Category[] = ["BUILDING", "INFANTRY", "WHEELED", "ARMORED", "AIR"];
const factionIndex = (owner: Faction) => ["EFA", "REDCAT", "GREEN", "BLUE"].indexOf(owner);
const typeHash = (type: PieceType) => [...type].reduce((sum, char) => sum + char.charCodeAt(0), 0);
const centerDistance = (s: GameState, cell: Cell) => distance(s, cell[0], cell[1], Math.floor(s.width / 2), Math.floor(s.height / 2));
const allCells = (s: GameState): Cell[] => { const out: Cell[] = []; for (let row = 0; row < s.height; row++) for (let col = 0; col < s.width; col++) if (inside(s, col, row)) out.push([col, row]); return out; };
const enemiesOf = (s: GameState, owner: Faction) => s.pieces.filter(piece => areEnemies(s, owner, piece.owner) && !piece.entrenched);
const alliesOf = (s: GameState, owner: Faction) => s.pieces.filter(piece => piece.owner === owner || (piece.owner !== owner && s.teams[piece.owner] === s.teams[owner]));
const owned = (s: GameState, owner: Faction, type?: PieceType) => s.pieces.filter(piece => piece.owner === owner && (!type || piece.type === type));
const queued = (s: GameState, owner: Faction, type: PieceType) => s.players[owner].queue.filter(item => item.type === type).length;
const count = (s: GameState, owner: Faction, type: PieceType) => owned(s, owner, type).length + queued(s, owner, type);
const nearestDistance = (s: GameState, from: Cell, cells: Cell[], fallback = 99) => cells.length ? Math.min(...cells.map(cell => distance(s, ...from, ...cell))) : fallback;
const chooseByDie = <T>(s: GameState, values: T[]): T | undefined => values.length ? values[(rollD6(s) - 1) % values.length] : undefined;

function routeDistance(s: GameState, piece: GamePiece, from: Cell, goal: Cell): number {
  if (isAir(piece.type)) return distance(s, ...from, ...goal);
  const queue: Array<[number, number, number]> = [[...from, 0]], seen = new Set([key(...from)]);
  while (queue.length) {
    const [col, row, cost] = queue.shift()!;
    if (col === goal[0] && row === goal[1]) return cost;
    for (const [nextCol, nextRow] of neighbors(s, col, row)) {
      const cellKey = key(nextCol, nextRow);
      if (seen.has(cellKey)) continue;
      const terrain = s.terrain[nextRow][nextCol];
      const occupied = piecesAt(s, nextCol, nextRow).filter(other => other.id !== piece.id);
      const isGoal = nextCol === goal[0] && nextRow === goal[1];
      const blockedTerrain = terrain.includes("MOUNTAIN") || (terrain === "WATER" && !s.bridges[nextRow][nextCol]) || s.civilBuilding[nextRow][nextCol] || (terrain.includes("FOREST") && PIECES[piece.type].notInForest);
      const blockedPiece = occupied.some(other => isBuilding(other.type) || (areEnemies(s, other.owner, piece.owner) && !(isArmored(piece.type) && PIECES[other.type].category === "INFANTRY")));
      if (!isGoal && (blockedTerrain || blockedPiece)) continue;
      if (isGoal && blockedTerrain) continue;
      seen.add(cellKey); queue.push([nextCol, nextRow, cost + 1]);
    }
  }
  return 999;
}

function canProvideShieldFollowUp(s: GameState, attacker: GamePiece, target: GamePiece): boolean {
  return alliesOf(s, attacker.owner).some(ally => {
    if (ally.id === attacker.id || ally.activated || !PIECES[ally.type].targets.includes(categoryForTarget(s, target))) return false;
    if (isCombatAircraft(ally.type) && !ally.ammoLoaded) return false;
    if (canAttack(s, ally, target)) return true;
    const old: Cell = [ally.col, ally.row];
    for (const cell of [...reachable(s, ally)].map(parseKey)) {
      ally.col = cell[0]; ally.row = cell[1];
      const possible = canAttack(s, ally, target);
      ally.col = old[0]; ally.row = old[1];
      if (possible) return true;
    }
    ally.col = old[0]; ally.row = old[1];
    return false;
  });
}

function basePieces(s: GameState, owner: Faction): GamePiece[] {
  const core = owned(s, owner).filter(piece => isBuilding(piece.type) && piece.type !== "MG_TOWER" && piece.type !== "RAK_TOWER");
  return core.length ? core : owned(s, owner);
}

function baseCenter(s: GameState, owner: Faction): Cell {
  const pieces = basePieces(s, owner);
  if (!pieces.length) return [Math.floor(s.width / 2), Math.floor(s.height / 2)];
  return [Math.round(pieces.reduce((sum, piece) => sum + piece.col, 0) / pieces.length), Math.round(pieces.reduce((sum, piece) => sum + piece.row, 0) / pieces.length)];
}

export function analyzeAiStrategy(s: GameState, owner = s.currentFaction): AiAnalysis {
  const enemy = enemiesOf(s, owner), base = baseCenter(s, owner), composition = Object.fromEntries(categories.map(category => [category, 0])) as Record<Category, number>;
  for (const piece of enemy) composition[categoryForTarget(s, piece)]++;
  const profileIndex = Math.abs((s.seed ^ (factionIndex(owner) + 1) * 7919) % 3);
  return {
    profile: (["RUSH", "BALANCED", "ECONOMY"] as const)[profileIndex],
    mapScale: Math.max(s.width, s.height) <= 11 ? "SMALL" : Math.max(s.width, s.height) <= 16 ? "MEDIUM" : "LARGE",
    oreRemaining: s.ore.flat().filter(Boolean).length,
    nearestThreat: nearestDistance(s, base, enemy.map(piece => [piece.col, piece.row])),
    enemyComposition: composition,
  };
}

function threatAt(s: GameState, owner: Faction, cell: Cell, category: Category, carryingOre = false): number {
  let danger = 0;
  for (const enemy of enemiesOf(s, owner)) {
    if (!PIECES[enemy.type].targets.includes(category)) continue;
    const reach = PIECES[enemy.type].speed + PIECES[enemy.type].maxRange, gap = distance(s, ...cell, enemy.col, enemy.row);
    if (gap <= reach) danger += (reach - gap + 1) * (PIECES[enemy.type].cost + 2) * (carryingOre ? 16 : 8);
  }
  return danger;
}

function targetValue(s: GameState, attacker: GamePiece, target: GamePiece): number {
  const definition = PIECES[target.type];
  let score = definition.cost * 34;
  if (target.type === "CONSTRUCTION_YARD" || target.type === "MCV") score += 420;
  if (target.type === "REFINERY") score += 280 + (s.players[target.owner].credits > 5 ? 80 : 0);
  if (target.type === "POWER_PLANT") score += 330 + (energy(s, target.owner) <= 1 ? 180 : 0);
  if (target.type === "FACTORY" || target.type === "BARRACKS") score += 210;
  if (target.type === "UPLINK") score += 230;
  if (target.type === "ING_INFANTRY" && basePieces(s, attacker.owner).some(base => distance(s, base.col, base.row, target.col, target.row) <= 4)) score += 300;
  if (target.type === "HARVESTER" && target.carryingOre) score += 170;
  if (target.hitsThisTurn) score += 250;
  if (target.suppressed) score += 50;
  if (definition.shield && !target.hitsThisTurn && attacker.type !== "MOBILE_FORTRESS" && !isDrone(s, attacker)) {
    const followUp = canProvideShieldFollowUp(s, attacker, target);
    score += followUp ? 90 : (attacker.type === "BOMBER" ? -1500 : -850);
  } else score += 210;
  if (canRetaliate(s, target, attacker)) score -= PIECES[attacker.type].cost * 55 + (PIECES[attacker.type].shield ? 20 : 100);
  return score;
}

function bestAttack(s: GameState, piece: GamePiece): { target: GamePiece; score: number } | undefined {
  return enemiesOf(s, piece.owner).filter(target => canAttack(s, piece, target)).map(target => ({ target, score: targetValue(s, piece, target) })).filter(choice => choice.score > 0).sort((a, b) => b.score - a.score)[0];
}

function bestSuppression(s: GameState, piece: GamePiece): { target: GamePiece; score: number } | undefined {
  return enemiesOf(s, piece.owner).filter(target => canSuppress(s, piece, target) && !target.suppressed).map(target => ({ target, score: targetValue(s, piece, target) })).filter(choice => choice.score > 0).sort((a, b) => b.score - a.score)[0];
}

function bestBridgeAttack(s: GameState, piece: GamePiece): { cell: Cell; score: number } | undefined {
  const ownGround = owned(s, piece.owner).filter(unit => !isAir(unit.type));
  return allCells(s).filter(([col, row]) => canAttackBridge(s, piece, col, row)).map(cell => {
    const occupants = piecesAt(s, ...cell), enemyVictims = occupants.filter(target => areEnemies(s, piece.owner, target.owner) && !isAir(target.type));
    const friendlyVictims = occupants.filter(target => !areEnemies(s, piece.owner, target.owner) && !isAir(target.type));
    const enemyApproach = nearestDistance(s, cell, enemiesOf(s, piece.owner).filter(target => !isAir(target.type)).map(target => [target.col, target.row]));
    const friendlyApproach = nearestDistance(s, cell, ownGround.map(target => [target.col, target.row]));
    return { cell, score: enemyVictims.reduce((sum, target) => sum + targetValue(s, piece, target), enemyVictims.length ? 700 : 0) - friendlyVictims.length * 1200 + (enemyApproach + 1 < friendlyApproach ? 140 : -220) };
  }).filter(choice => choice.score > 0).sort((a, b) => b.score - a.score)[0];
}

function strategicTarget(s: GameState, piece: GamePiece): GamePiece | undefined {
  const candidates = enemiesOf(s, piece.owner).filter(target => piece.type === "ING_INFANTRY" ? isBuilding(target.type) || target.type === "MCV" : PIECES[piece.type].targets.includes(categoryForTarget(s, target)));
  return candidates.map(target => { const route = routeDistance(s, piece, [piece.col, piece.row], [target.col, target.row]); return { target, score: targetValue(s, piece, target) - route * 22 - (route >= 999 ? 5000 : 0) }; }).sort((a, b) => b.score - a.score)[0]?.target;
}

function nearestOre(s: GameState, piece: GamePiece): Cell | undefined {
  const ore = allCells(s).filter(([col, row]) => s.ore[row][col]);
  if (!ore.length) return undefined;
  return ore.map(cell => { const route = routeDistance(s, piece, [piece.col, piece.row], cell); return { cell, score: -route * 30 - (route >= 999 ? 5000 : 0) - threatAt(s, piece.owner, cell, "ARMORED") }; }).sort((a, b) => b.score - a.score)[0].cell;
}

function refineryGoal(s: GameState, piece: GamePiece): Cell | undefined {
  const outputs = owned(s, piece.owner, "REFINERY").map(refinery => refineryOutput(s, refinery)).filter((cell): cell is Cell => !!cell);
  return outputs.map(cell => { const route = routeDistance(s, piece, [piece.col, piece.row], cell); return { cell, score: -route * 35 - (route >= 999 ? 5000 : 0) - threatAt(s, piece.owner, cell, "ARMORED", true) }; }).sort((a, b) => b.score - a.score)[0]?.cell;
}

function nearestHelipad(s: GameState, piece: GamePiece): Cell | undefined {
  const pads = owned(s, piece.owner, "HELIPAD").map(pad => [pad.col, pad.row] as Cell);
  return pads.sort((a, b) => distance(s, piece.col, piece.row, ...a) - distance(s, piece.col, piece.row, ...b))[0];
}

function crushScore(s: GameState, piece: GamePiece, cell: Cell): number {
  if (!isArmored(piece.type)) return 0;
  return piecesAt(s, ...cell).filter(target => areEnemies(s, piece.owner, target.owner) && PIECES[target.type].category === "INFANTRY").reduce((score, target) => {
    const retaliation = PIECES[target.type].targets.includes("ARMORED") && !target.suppressed && !target.retaliatedThisTurn;
    const defensive = target.type === "ING_INFANTRY" && basePieces(s, piece.owner).some(base => distance(s, base.col, base.row, target.col, target.row) <= 4);
    return score + PIECES[target.type].cost * 420 + (defensive ? 1100 : 0) - (retaliation ? (PIECES[piece.type].shield ? 100 : 600) : 0);
  }, 0);
}

function actionOpportunity(s: GameState, piece: GamePiece): number {
  let score = bestAttack(s, piece)?.score ?? 0;
  if (canHarvest(s, piece) || canUnload(s, piece)) score = Math.max(score, 620);
  if (canReload(s, piece)) score = Math.max(score, 520);
  if (piece.type === "ING_INFANTRY") {
    const captures = enemiesOf(s, piece.owner).filter(target => canCapture(s, piece, target));
    if (captures.length) score = Math.max(score, 900 + Math.max(...captures.map(target => targetValue(s, piece, target))));
    if (allCells(s).some(cell => canCaptureCivil(s, piece, ...cell))) score = Math.max(score, 760);
    if (allCells(s).some(cell => canRepairBridge(s, piece, ...cell))) score = Math.max(score, 260);
  }
  if (isTransport(piece.type) && piece.cargo.some(Boolean) && allCells(s).some(cell => canUnboard(s, piece, ...cell))) score = Math.max(score, 320);
  if (PIECES[piece.type].category === "INFANTRY" && owned(s, piece.owner).some(transport => canBoard(s, piece, transport))) score = Math.max(score, 180);
  return score;
}

function bestCrushGoal(s: GameState, piece: GamePiece): Cell | undefined {
  if (!isArmored(piece.type)) return undefined;
  const candidates = enemiesOf(s, piece.owner).filter(target => PIECES[target.type].category === "INFANTRY").map(target => {
    const route = routeDistance(s, piece, [piece.col, piece.row], [target.col, target.row]);
    const canFightBack = PIECES[target.type].targets.includes("ARMORED");
    const defensive = target.type === "ING_INFANTRY" && basePieces(s, piece.owner).some(base => distance(s, base.col, base.row, target.col, target.row) <= 5);
    const protectsRak = target.type === "MG_INFANTRY" && owned(s, piece.owner, "RAK_INFANTRY").some(ally => distance(s, ally.col, ally.row, target.col, target.row) <= 4);
    return { target, route, score: PIECES[target.type].cost * 180 + (defensive ? 900 : 0) + (protectsRak ? 650 : 0) - (canFightBack && !PIECES[piece.type].shield ? 700 : 0) - route * 45 };
  }).filter(choice => choice.route < 999).sort((a,b) => b.score - a.score);
  const best = candidates[0];
  if (!best) return undefined;
  const oreAvailable = s.ore.some(row => row.some(Boolean));
  if (piece.type === "HARVESTER" && oreAvailable && !best.score.toString().includes("NaN") && best.score < 700) return undefined;
  return [best.target.col, best.target.row];
}

function movementGoal(s: GameState, piece: GamePiece): Cell | undefined {
  const crush = bestCrushGoal(s, piece);
  if (crush && (piece.type !== "HARVESTER" || !piece.carryingOre)) return crush;
  if (piece.type === "HARVESTER") return piece.carryingOre ? refineryGoal(s, piece) : nearestOre(s, piece);
  if (isCombatAircraft(piece.type) && !piece.ammoLoaded) return nearestHelipad(s, piece);
  if (isTransport(piece.type) && piece.cargo.length) { const target = enemiesOf(s, piece.owner).sort((a, b) => distance(s, piece.col, piece.row, a.col, a.row) - distance(s, piece.col, piece.row, b.col, b.row))[0]; if (target) return [target.col, target.row]; }
  const target = strategicTarget(s, piece);
  return target ? [target.col, target.row] : undefined;
}

export function chooseAiMove(s: GameState, piece: GamePiece): Cell {
  const current: Cell = [piece.col, piece.row], candidates = [current, ...[...reachable(s, piece)].map(parseKey)], goal = movementGoal(s, piece);
  const oreExists = s.ore.some(row => row.some(Boolean));
  return candidates.map(cell => {
    const old: Cell = [piece.col, piece.row]; piece.col = cell[0]; piece.row = cell[1];
    let score = actionOpportunity(s, piece) * 1.4 + crushScore(s, piece, cell);
    if (goal) { const route = routeDistance(s, piece, cell, goal); score -= route * (piece.type === "HARVESTER" ? 55 : 34); if (route >= 999) score -= 5000; }
    if (piece.type === "HARVESTER") {
      score -= threatAt(s, piece.owner, cell, "ARMORED", piece.carryingOre) * 1.7;
      if (!oreExists) score += crushScore(s, piece, cell) * 1.5;
    } else score -= threatAt(s, piece.owner, cell, categoryForTarget(s, piece)) * (isAir(piece.type) ? 2.2 : PIECES[piece.type].shield ? .45 : .85);
    if (isAir(piece.type)) score += centerDistance(s, cell) * -1;
    if (cell[0] === old[0] && cell[1] === old[1] && goal && distance(s, ...old, ...goal) > PIECES[piece.type].maxRange) score -= 35;
    piece.col = old[0]; piece.row = old[1];
    return { cell, score };
  }).sort((a, b) => b.score - a.score || centerDistance(s, a.cell) - centerDistance(s, b.cell))[0]?.cell ?? current;
}

function performMovement(s: GameState, piece: GamePiece): void {
  if (PIECES[piece.type].speed <= 0 || piece.movementUsed) return;
  const destination = chooseAiMove(s, piece);
  if (destination[0] === piece.col && destination[1] === piece.row) confirmMovement(s, piece);
  else move(s, piece, ...destination, true);
}

function energyRecoveryPlan(s: GameState, owner: Faction): GamePiece[] {
  if (energy(s, owner) >= 0 || !owned(s, owner, "HARVESTER").some(piece => piece.carryingOre) || !owned(s, owner, "REFINERY").length) return [];
  const counts = new Map<PieceType, number>();
  for (const piece of owned(s, owner)) counts.set(piece.type, (counts.get(piece.type) ?? 0) + 1);
  const candidates = owned(s, owner).filter(piece => !piece.activated && isBuilding(piece.type) && PIECES[piece.type].consumesEnergy && piece.capturedRound !== s.round && !(piece.type === "REFINERY" && (counts.get("REFINERY") ?? 0) <= 1) && !(piece.type === "HELIPAD" && piecesAt(s, piece.col, piece.row).some(other => other.id !== piece.id && isCombatAircraft(other.type)))).sort((a, b) => {
    const score = (piece: GamePiece) => piece.type === "REFINERY" ? 0 : piece.type === "RAK_TOWER" ? 10 : (counts.get(piece.type) ?? 0) > 1 ? 20 : piece.type === "HELIPAD" ? 55 : piece.type === "BARRACKS" ? 65 : piece.type === "FACTORY" ? 70 : piece.type === "UPLINK" ? 90 : 75;
    return score(a) - score(b) || PIECES[a.type].cost - PIECES[b.type].cost || a.id - b.id;
  });
  const deficit = -energy(s, owner), queuedPlants = s.players[owner].queue.filter(item => item.type === "POWER_PLANT").length;
  let requiredSales = candidates.length >= deficit ? deficit : Math.max(0, -(energy(s, owner) + Math.max(1, queuedPlants) * 4));
  let requiredCredits = queuedPlants ? 0 : Math.max(0, PIECES.POWER_PLANT.cost - s.players[owner].credits), refund = 0;
  const plan: GamePiece[] = [];
  for (const piece of candidates) {
    if (plan.length >= requiredSales && refund >= requiredCredits) break;
    plan.push(piece);
    refund += piece.type === "REFINERY" ? 1 : Math.ceil(PIECES[piece.type].cost / 2);
  }
  return plan;
}

function bestCapture(s: GameState, engineer: GamePiece): GamePiece | undefined {
  return enemiesOf(s, engineer.owner).filter(target => canCapture(s, engineer, target)).sort((a, b) => targetValue(s, engineer, b) - targetValue(s, engineer, a))[0];
}

function performAction(s: GameState, piece: GamePiece, retaliationDecision: RetaliationDecision = () => true): boolean {
  if (isBuilding(piece.type) && energyRecoveryPlan(s, piece.owner)[0]?.id === piece.id) return sell(s, piece).success;
  if (piece.type === "UPLINK" && s.players[piece.owner].orbitalCooldown === 0) {
    const target = enemiesOf(s, piece.owner).filter(candidate => canOrbitalStrike(s, candidate, piece.owner)).map(candidate => ({ candidate, score: PIECES[candidate.type].cost * 40 + (candidate.type === "CONSTRUCTION_YARD" ? 500 : 0) + (candidate.hitsThisTurn ? 220 : 0) })).sort((a, b) => b.score - a.score)[0]?.candidate;
    if (target) return orbitalStrike(s, target).success;
  }
  if (canHarvest(s, piece) || canUnload(s, piece)) return harvesterAction(s, piece).success;
  if (canReload(s, piece)) return reload(s, piece).success;
  if (piece.type === "ING_INFANTRY") {
    const target = bestCapture(s, piece); if (target) {
      const queue = s.players[target.owner].queue.filter(item => target.type === "CONSTRUCTION_YARD" ? isBuilding(item.type) : target.type === "BARRACKS" ? PIECES[item.type].category === "INFANTRY" : target.type === "FACTORY" ? !isBuilding(item.type) && PIECES[item.type].category !== "INFANTRY" : false).sort((a, b) => PIECES[b.type].cost - PIECES[a.type].cost);
      return capture(s, piece, target, queue[0]?.id ?? -1).success;
    }
    const civil = allCells(s).find(cell => canCaptureCivil(s, piece, ...cell)); if (civil) return captureCivil(s, piece, ...civil).success;
    const bridge = allCells(s).find(cell => canRepairBridge(s, piece, ...cell)); if (bridge) return repairBridge(s, piece, ...bridge).success;
  }
  if (isTransport(piece.type) && piece.cargo.length > piece.cargoBoardedThisTurn) {
    const target = enemiesOf(s, piece.owner).sort((a, b) => distance(s, piece.col, piece.row, a.col, a.row) - distance(s, piece.col, piece.row, b.col, b.row))[0], cells = neighbors(s, piece.col, piece.row).filter(cell => canUnboard(s, piece, ...cell));
    const chosen = cells.sort((a, b) => target ? distance(s, ...a, target.col, target.row) - distance(s, ...b, target.col, target.row) : centerDistance(s, a) - centerDistance(s, b))[0];
    if (chosen) return unboard(s, piece, ...chosen).success;
  }
  if (PIECES[piece.type].category === "INFANTRY") {
    const target = owned(s, piece.owner).filter(transport => canBoard(s, piece, transport)).sort((a, b) => PIECES[b.type].speed - PIECES[a.type].speed)[0];
    const strategic = strategicTarget(s, piece);
    if (target && strategic && distance(s, piece.col, piece.row, strategic.col, strategic.row) > 4) return board(s, piece, target).success;
  }
  const shot = bestAttack(s, piece), suppression = bestSuppression(s, piece), bridge = bestBridgeAttack(s, piece);
  if (bridge && bridge.score > (shot?.score ?? 0) + 80) return attackBridge(s, piece, ...bridge.cell).success;
  if (shot) {
    const retaliationRisk = canRetaliate(s, shot.target, piece), followUp = canProvideShieldFollowUp(s, piece, shot.target);
    if (retaliationRisk && followUp && !shot.target.suppressed && PIECES[piece.type].cost <= PIECES[shot.target.type].cost) return suppress(s, piece, shot.target).success;
    return attackWithDecision(s,piece,shot.target,retaliationDecision);
  }
  if (suppression) return suppress(s, piece, suppression.target).success;
  return false;
}

function activateDrone(s: GameState, drone: GamePiece, retaliationDecision: RetaliationDecision): void {
  if (drone.tilted) { drone.tilted = false; finishActivation(s, drone); return; }
  const candidates = s.pieces.filter(target => target.owner === s.humanFaction && !target.entrenched && PIECES[drone.type].targets.includes(categoryForTarget(s, target)));
  const closest = candidates.length ? Math.min(...candidates.map(target => distance(s, drone.col, drone.row, target.col, target.row))) : 99;
  const tied = candidates.filter(target => distance(s, drone.col, drone.row, target.col, target.row) === closest), target = tied.length === 1 ? tied[0] : chooseByDie(s, tied);
  if (target) {
    const cells = [...reachable(s, drone)].map(parseKey), attackCells = cells.filter(cell => { const range = distance(s, ...cell, target.col, target.row); return range >= PIECES[drone.type].minRange && range <= PIECES[drone.type].maxRange; });
    const pool = attackCells.length ? attackCells : cells;
    const bestDistance = pool.length ? Math.min(...pool.map(cell => distance(s, ...cell, target.col, target.row))) : 99;
    const best = pool.filter(cell => distance(s, ...cell, target.col, target.row) === bestDistance), destination = best.length === 1 ? best[0] : chooseByDie(s, best);
    if (destination) move(s, drone, ...destination, true); else confirmMovement(s, drone);
  } else confirmMovement(s, drone);
  if (s.pieces.includes(drone)) { const targetInRange = enemiesOf(s, drone.owner).filter(candidate => canAttack(s, drone, candidate)).sort((a, b) => distance(s, drone.col, drone.row, a.col, a.row) - distance(s, drone.col, drone.row, b.col, b.row))[0]; if (targetInRange) attackWithDecision(s,drone,targetInRange,retaliationDecision); }
  if(s.pendingRetaliation)return;
  if (s.pieces.includes(drone)) finishActivation(s, drone);
}

function activateMcv(s: GameState, piece: GamePiece): void {
  const canDeploy=(col:number,row:number)=>s.terrain[row]?.[col]!=="FORD"&&!s.bridges[row]?.[col];
  if(canDeploy(piece.col,piece.row)){if(!piece.movementUsed)confirmMovement(s,piece);transform(s,piece);return;}
  const analysis=analyzeAiStrategy(s,piece.owner),ore=allCells(s).filter(([col,row])=>s.ore[row][col]);
  const chosen=[...reachable(s,piece)].map(parseKey).filter(([col,row])=>canDeploy(col,row)).map(cell=>({cell,score:-nearestDistance(s,cell,ore)*20-threatAt(s,piece.owner,cell,"ARMORED")-centerDistance(s,cell)*2})).sort((a,b)=>b.score-a.score)[0]?.cell;
  if(chosen)move(s,piece,...chosen,true);else confirmMovement(s,piece);
  finishActivation(s,piece);s.event=chosen?"MCV moved to a valid deployment space and must wait until its next Turn to transform.":"MCV cannot find a valid deployment space.";
}

function activate(s: GameState, piece: GamePiece, retaliationDecision: RetaliationDecision = () => true): void {
  if (isDrone(s, piece)) { activateDrone(s, piece, retaliationDecision); return; }
  if (piece.tilted) { piece.tilted = false; finishActivation(s, piece); return; }
  if (piece.type === "MCV" && !s.mission.mode) { activateMcv(s, piece); return; }
  if (piece.type === "ARTILLERY") {
    const hasShot = !!bestAttack(s, piece) || !!bestBridgeAttack(s, piece);
    if (hasShot) { confirmMovement(s, piece); performAction(s, piece, retaliationDecision); } else performMovement(s, piece);
    if(s.pendingRetaliation)return;
    if (s.pieces.includes(piece)) finishActivation(s, piece);
    return;
  }
  performMovement(s, piece);
  if (!s.pieces.includes(piece) || s.phase === "VICTORY") return;
  performAction(s, piece, retaliationDecision);
  if(s.pendingRetaliation)return;
  if (s.pieces.includes(piece)) finishActivation(s, piece);
}

function outputFacings(s: GameState, type: PieceType, col: number, row: number): number[] {
  const result: number[] = [];
  for (let facing = 0; facing < 6; facing++) {
    const first = directedNeighbor(s.orientation, col, row, facing), second = directedNeighbor(s.orientation, col, row, facing + 1);
    if (validBuildingOutput(s, ...first) && (type !== "FACTORY" || validBuildingOutput(s, ...second))) result.push(facing);
  }
  return result;
}

function openBarracksExits(s: GameState, owner: Faction, blocked?: Cell): number {
  return owned(s, owner, "BARRACKS").reduce((sum, barracks) => sum + neighbors(s, barracks.col, barracks.row).filter(([col, row]) => (!blocked || col !== blocked[0] || row !== blocked[1]) && piecesAt(s, col, row).length === 0 && !s.bridges[row][col] && s.terrain[row][col] !== "WATER" && !s.terrain[row][col].includes("MOUNTAIN")).length, 0);
}

function openAreaFrom(s: GameState, start: Cell, blocked: Cell): number {
  if (start[0] === blocked[0] && start[1] === blocked[1]) return 0;
  const queue: Cell[] = [start], seen = new Set([key(...start)]);
  while (queue.length && seen.size < 18) {
    const current = queue.shift()!;
    for (const next of neighbors(s, ...current)) {
      const k = key(...next), terrain = s.terrain[next[1]][next[0]];
      if (seen.has(k) || (next[0] === blocked[0] && next[1] === blocked[1]) || terrain.includes("MOUNTAIN") || (terrain === "WATER" && !s.bridges[next[1]][next[0]]) || s.civilBuilding[next[1]][next[0]] || piecesAt(s, ...next).some(piece => isBuilding(piece.type))) continue;
      seen.add(k); queue.push(next);
    }
  }
  return seen.size;
}

function placementAccess(s: GameState, type: PieceType, cell: Cell): number {
  if (!["BARRACKS", "FACTORY", "REFINERY"].includes(type)) return 18;
  const exits = neighbors(s, ...cell).filter(([col, row]) => !s.terrain[row][col].includes("MOUNTAIN") && (s.terrain[row][col] !== "WATER" || !!s.bridges[row][col]) && !s.civilBuilding[row][col] && !piecesAt(s, col, row).some(piece => isBuilding(piece.type)));
  return exits.length ? Math.max(...exits.map(exit => openAreaFrom(s, exit, cell))) : 0;
}

function buildingScore(s: GameState, type: PieceType, owner: Faction, cell: Cell, analysis: AiAnalysis): number {
  const base = baseCenter(s, owner), enemies = enemiesOf(s, owner), enemyCells = enemies.map(piece => [piece.col, piece.row] as Cell), ore = allCells(s).filter(([col, row]) => s.ore[row][col]);
  const enemyGap = nearestDistance(s, cell, enemyCells), baseGap = distance(s, ...cell, ...base), edge = Math.min(cell[0], cell[1], s.width - 1 - cell[0], s.height - 1 - cell[1]);
  const adjacentBuildings = owned(s, owner).filter(piece => isBuilding(piece.type) && distance(s, piece.col, piece.row, ...cell) === 1).length;
  const access = placementAccess(s, type, cell);
  let score = -baseGap * 10 + edge * 4 - adjacentBuildings * 220 + (baseGap === 2 ? 170 : 0);
  if (["BARRACKS", "FACTORY", "REFINERY"].includes(type)) score += access * 28 - (access < 8 ? 2200 : 0);
  if (type === "REFINERY") score += 520 - nearestDistance(s, cell, ore) * 65 - threatAt(s, owner, cell, "BUILDING") * .8;
  else if (type === "BARRACKS" || type === "FACTORY") score += 260 - enemyGap * (analysis.mapScale === "SMALL" ? 16 : 8) - threatAt(s, owner, cell, "BUILDING") * .35;
  else if (type === "HELIPAD") { const pads = owned(s, owner, "HELIPAD"); score += pads.length ? baseGap * 72 - enemyGap * 10 - threatAt(s, owner, cell, "BUILDING") * 1.1 : 260 - baseGap * 30 + enemyGap * 3; }
  else if (type === "MG_TOWER" || type === "RAK_TOWER") score += 360 - enemyGap * 32 - baseGap * 6;
  else score += enemyGap * 8 - threatAt(s, owner, cell, "BUILDING") * .8;
  if (owned(s, owner, "BARRACKS").some(barracks => distance(s, barracks.col, barracks.row, ...cell) === 1) && openBarracksExits(s, owner, cell) < 3) score -= 1800;
  if (owned(s, owner).some(building => (building.type === "FACTORY" || building.type === "REFINERY") && distance(s, building.col, building.row, ...cell) === 1)) score -= 1200;
  if (baseGap > Math.max(5, Math.ceil(Math.max(s.width, s.height) / 3))) score -= 300;
  return score;
}

function unitScore(s: GameState, type: PieceType, owner: Faction, cell: Cell): number {
  const enemy = enemiesOf(s, owner), targets = enemy.filter(target => PIECES[type].targets.includes(categoryForTarget(s, target))), targetGap = nearestDistance(s, cell, targets.map(target => [target.col, target.row] as Cell));
  let score = -targetGap * 30 - threatAt(s, owner, cell, PIECES[type].category) * .5;
  if (type === "ING_INFANTRY") score = -nearestDistance(s, cell, enemy.filter(target => isBuilding(target.type)).map(target => [target.col, target.row] as Cell)) * 45;
  if (type === "HARVESTER") score = -nearestDistance(s, cell, allCells(s).filter(([col, row]) => s.ore[row][col])) * 35 - threatAt(s, owner, cell, "ARMORED");
  if (isCombatAircraft(type) && piecesAt(s, ...cell).some(piece => piece.type === "HELIPAD")) score += 90;
  return score;
}

export function chooseAiDeployment(s: GameState, type: PieceType, owner = s.currentFaction): AiDeploymentChoice | undefined {
  const analysis = analyzeAiStrategy(s, owner), candidates = allCells(s).filter(([col, row]) => validDeployment(s, type, owner, col, row)).map(cell => {
    const facings = type === "REFINERY" || type === "FACTORY" ? outputFacings(s, type, ...cell) : [];
    if ((type === "REFINERY" || type === "FACTORY") && !facings.length) return undefined;
    let facing: number | undefined;
    if (facings.length) {
      const goals = type === "REFINERY" ? allCells(s).filter(([col, row]) => s.ore[row][col]) : enemiesOf(s, owner).map(target => [target.col, target.row] as Cell);
      facing = facings.map(value => { const outputs = [directedNeighbor(s.orientation, ...cell, value), ...(type === "FACTORY" ? [directedNeighbor(s.orientation, ...cell, value + 1)] : [])]; return { value, score: -Math.min(...outputs.map(output => nearestDistance(s, output, goals))) - outputs.reduce((sum, output) => sum + threatAt(s, owner, output, type === "REFINERY" ? "ARMORED" : "ARMORED"), 0) * .01 }; }).sort((a, b) => b.score - a.score)[0]?.value;
    }
    return { col: cell[0], row: cell[1], facing, score: isBuilding(type) ? buildingScore(s, type, owner, cell, analysis) : unitScore(s, type, owner, cell) };
  }).filter((choice): choice is { col: number; row: number; facing: number | undefined; score: number } => !!choice).sort((a, b) => b.score - a.score);
  return candidates[0];
}

function deployReady(s: GameState): void {
  const player = s.players[s.currentFaction];
  const ready = [...player.queue].filter(item => item.remaining === 0).sort((a, b) => (isBuilding(a.type) ? -1 : 0) - (isBuilding(b.type) ? -1 : 0) || a.id - b.id);
  for (const item of ready) {
    const choice = chooseAiDeployment(s, item.type); if (!choice) { recycle(s, item.id); continue; }
    if (deploy(s, item.id, choice.col, choice.row).success) {
      const placed = s.pieces.at(-1)!;
      if ((placed.type === "REFINERY" || placed.type === "FACTORY") && choice.facing !== undefined) configureOutput(s, placed, choice.facing);
    }
  }
}

function productionScore(s: GameState, type: PieceType, analysis: AiAnalysis): number {
  const owner = s.currentFaction, existing = count(s, owner, type), enemy = analysis.enemyComposition, near = analysis.nearestThreat <= (analysis.mapScale === "SMALL" ? 6 : 8), profile = analysis.profile;
  const ownInfantry = owned(s, owner).filter(piece => PIECES[piece.type].category === "INFANTRY").length, ownAircraft = owned(s, owner).filter(piece => isCombatAircraft(piece.type)).length, enemyTotal = Object.values(enemy).reduce((sum, value) => sum + value, 0), obstructed = s.terrain.flat().filter(terrain => terrain.includes("MOUNTAIN") || terrain === "WATER").length / Math.max(1, s.width * s.height);
  let score = 0;
  if (type === "POWER_PLANT") score = existing === 0 ? 1600 : energy(s, owner) < 1 ? 1200 : existing < 2 && energy(s, owner) < 3 ? 360 : -200;
  else if (type === "REFINERY") score = analysis.oreRemaining === 0 ? -500 : existing === 0 ? 1050 : existing === 1 && analysis.oreRemaining >= 6 ? (profile === "ECONOMY" ? 820 : near ? 360 : 620) : -180;
  else if (type === "BARRACKS") score = existing === 0 ? 900 + (near || analysis.mapScale === "SMALL" || profile === "RUSH" ? 260 : 0) : existing === 1 && analysis.mapScale === "LARGE" ? 250 : -120;
  else if (type === "FACTORY") score = existing === 0 ? 760 + enemy.ARMORED * 35 + enemy.BUILDING * 20 : existing === 1 && analysis.mapScale === "LARGE" ? 230 : -140;
  else if (type === "MG_TOWER") score = existing >= 2 ? -700 : near ? 370 + enemy.INFANTRY * 55 + enemy.WHEELED * 35 - existing * 240 : -180;
  else if (type === "RAK_TOWER") score = existing >= 2 ? -700 : near ? 390 + enemy.ARMORED * 50 + enemy.AIR * 80 - existing * 250 : -160;
  else if (type === "HELIPAD") score = s.techLevel < 4 || count(s, owner, "FACTORY") === 0 ? -500 : existing === 0 ? 520 + ownAircraft * 60 : existing === 1 && analysis.mapScale !== "SMALL" && ownAircraft >= 2 ? 470 : -180;
  else if (type === "UPLINK") score = s.techLevel >= 5 && existing === 0 && count(s, owner, "POWER_PLANT") >= 2 ? 300 + enemy.BUILDING * 35 : -300;
  else if (type === "HARVESTER") score = analysis.oreRemaining === 0 || count(s, owner, "REFINERY") === 0 ? -700 : existing === 0 ? 1250 : existing < Math.min(2, count(s, owner, "REFINERY")) ? 680 : -220;
  else if (type === "MG_INFANTRY") score = 360 + enemy.INFANTRY * 90 + enemy.WHEELED * 35 + (profile === "RUSH" ? 90 : 0);
  else if (type === "RAK_INFANTRY") score = 220 + enemy.ARMORED * 75 + enemy.AIR * 90 + enemy.BUILDING * 35 - Math.max(0, existing - 2) * 120;
  else if (type === "ING_INFANTRY") score = 150 + enemy.BUILDING * 60 + (analysis.mapScale === "SMALL" ? 80 : 0) - existing * 45;
  else if (type === "MG_BUGGY") score = 270 + enemy.INFANTRY * 65 + enemy.WHEELED * 35;
  else if (type === "TANK") score = 390 + enemy.ARMORED * 65 + enemy.BUILDING * 55 + enemy.INFANTRY * 15;
  else if (type === "ARTILLERY") score = s.techLevel >= 3 ? 230 + enemy.BUILDING * 80 + enemy.ARMORED * 45 : -500;
  else if (type === "MOBILE_FORTRESS") score = s.techLevel >= 5 && existing < 1 && enemyTotal >= 3 ? 610 + enemyTotal * 38 + enemy.BUILDING * 25 : s.techLevel >= 5 ? 260 - existing * 170 : -500;
  else if (type === "APC") score = analysis.mapScale !== "SMALL" && ownInfantry >= 2 && existing < 1 ? 430 + enemy.INFANTRY * 30 + (profile === "RUSH" ? 80 : 0) : 45 - existing * 110;
  else if (type === "DROPSHIP") score = s.techLevel >= 4 && ownInfantry >= 2 && existing < 1 && (analysis.mapScale === "LARGE" || obstructed > .18) ? 470 + Math.round(obstructed * 500) : -120 - existing * 100;
  else if (type === "FIGHTER") score = s.techLevel >= 4 && count(s, owner, "HELIPAD") > 0 ? 420 + enemy.AIR * 125 + enemy.INFANTRY * 48 - existing * 70 : -500;
  else if (type === "BOMBER") score = s.techLevel >= 4 && count(s, owner, "HELIPAD") > 0 ? 320 + enemy.ARMORED * 75 + enemy.BUILDING * 70 : -500;
  score -= existing * 35 + PIECES[type].cost * 4;
  score += ((typeHash(type) + s.round * 17 + s.seed + factionIndex(owner) * 13) % 41) - 20;
  return score;
}

export function rankAiProduction(s: GameState): PieceType[] {
  const analysis = analyzeAiStrategy(s), candidates = Object.keys(PIECES) as PieceType[];
  return candidates.filter(type => PIECES[type].tech <= s.techLevel && count(s, s.currentFaction, type) < PIECES[type].maxCount).map(type => ({ type, score: productionScore(s, type, analysis) })).filter(item => item.score > 0).sort((a, b) => b.score - a.score).map(item => item.type);
}

function recoverPowerPlantEconomy(s: GameState): void {
  const owner = s.currentFaction, player = s.players[owner], plantCost = PIECES.POWER_PLANT.cost;
  if (count(s, owner, "POWER_PLANT") > 0 || count(s, owner, "CONSTRUCTION_YARD") === 0 || player.credits >= plantCost) return;
  const counts = new Map<PieceType, number>();
  for (const piece of owned(s, owner)) counts.set(piece.type, (counts.get(piece.type) ?? 0) + 1);
  const refund = (piece: GamePiece) => piece.type === "REFINERY" ? 1 : Math.ceil(PIECES[piece.type].cost / 2);
  const candidates = owned(s, owner).filter(piece => isBuilding(piece.type) && PIECES[piece.type].consumesEnergy && piece.capturedRound !== s.round && !(piece.type === "REFINERY" && (counts.get("REFINERY") ?? 0) <= 1) && !(piece.type === "HELIPAD" && piecesAt(s, piece.col, piece.row).some(other => other.id !== piece.id && isCombatAircraft(other.type))));
  while (player.credits < plantCost && candidates.length) {
    const needed = plantCost - player.credits;
    candidates.sort((a, b) => {
      const score = (piece: GamePiece) => (piece.type === "REFINERY" ? 0 : piece.type === "RAK_TOWER" ? 10 : (counts.get(piece.type) ?? 0) > 1 ? 20 : piece.type === "HELIPAD" ? 55 : piece.type === "BARRACKS" ? 65 : piece.type === "FACTORY" ? 70 : piece.type === "UPLINK" ? 90 : 75) - (refund(piece) >= needed ? 30 : 0);
      return score(a) - score(b) || refund(b) - refund(a) || a.id - b.id;
    });
    const piece = candidates.shift()!;
    if (!sell(s, piece).success) break;
    counts.set(piece.type, Math.max(0, (counts.get(piece.type) ?? 1) - 1));
  }
}

function recoverHarvesterEconomy(s: GameState): void {
  const owner = s.currentFaction, player = s.players[owner];
  if (!analyzeAiStrategy(s, owner).oreRemaining || count(s, owner, "HARVESTER") > 0 || !owned(s, owner, "FACTORY").length || !owned(s, owner, "REFINERY").length || player.credits >= PIECES.HARVESTER.cost) return;
  const expendable = owned(s, owner).filter(piece => isBuilding(piece.type) && (piece.type === "MG_TOWER" || piece.type === "RAK_TOWER" || owned(s, owner, piece.type).length > 1)).sort((a, b) => PIECES[b.type].cost - PIECES[a.type].cost)[0];
  if (expendable) sell(s, expendable);
}

function production(s: GameState): void {
  beginProduction(s);
  recoverPowerPlantEconomy(s);
  recoverHarvesterEconomy(s);
  const owner = s.currentFaction, player = s.players[owner];
  deployReady(s);
  const analysis = analyzeAiStrategy(s, owner), near = analysis.nearestThreat <= (analysis.mapScale === "SMALL" ? 6 : 8), activeWork = player.queue.filter(item => item.remaining > 0).length;
  let orderLimit = near || analysis.profile === "RUSH" ? 2 : 1;
  if (!near && activeWork >= 2 && player.credits < 8) orderLimit = 0;
  if (orderLimit <= 0) return;
  let placed = 0;
  for (const type of rankAiProduction(s)) {
    if (energy(s, owner) < 0 && type !== "POWER_PLANT") continue;
    const reserve = near || type === "POWER_PLANT" || type === "HARVESTER" ? 0 : analysis.profile === "ECONOMY" ? 3 : 1;
    if (player.credits - PIECES[type].cost < reserve) continue;
    if (order(s, type).success && ++placed >= orderLimit) break;
  }
}

export function playAiTurn(s: GameState): string {
  const owner = s.currentFaction;
  if (!s.aiPlayers[owner] && !(s.mission.mode && owner === "REDCAT")) return "";
  const recoveryIds = new Set(energyRecoveryPlan(s, owner).map(piece => piece.id));
  const priority = (piece: GamePiece) => {
    if (s.mission.mode && owner === "REDCAT") return piece.type === "FIGHTER" ? -100 : piece.type === "BOMBER" ? -90 : 0;
    if (recoveryIds.has(piece.id)) return -95;
    if (piece.type === "MCV") return -80;
    if (piece.type === "UPLINK" && s.players[owner].orbitalCooldown === 0) return -66;
    if (isBuilding(piece.type) && PIECES[piece.type].targets.length) return -65;
    if (piece.type === "RAK_INFANTRY" || piece.type === "MG_INFANTRY") return -45;
    if (piece.type === "HARVESTER") return -35;
    if (piece.type === "ING_INFANTRY") return -20;
    if (isTransport(piece.type)) return 10;
    return 0;
  };
  const figures = s.pieces.filter(piece => piece.owner === owner && !piece.activated && (!isBuilding(piece.type) || PIECES[piece.type].targets.length > 0 || recoveryIds.has(piece.id) || (piece.type === "UPLINK" && s.players[owner].orbitalCooldown === 0))).sort((a, b) => priority(a) - priority(b) || b.id - a.id);
  for (const piece of figures) { if (s.phase === "VICTORY") break; if (s.pieces.includes(piece)) activate(s, piece); }
  if ((s.phase as GameState["phase"]) !== "VICTORY") { if (!s.mission.mode && s.activePlayers[owner]) production(s); if ((s.phase as GameState["phase"]) !== "VICTORY" && s.currentFaction === owner) endTurn(s); }
  checkVictory(s);
  return s.mission.triggerReport || `${owner} AI completed its turn.`;
}


export interface AiStepResult { pieceId: number | null; from?: Cell; to?: Cell; completedTurn: boolean; message: string }

/** Executes exactly one visible AI activation. Production and end-turn are a separate final step. */
export function playAiStep(s: GameState, retaliationDecision: RetaliationDecision = () => true): AiStepResult {
  const owner = s.currentFaction;
  if (!s.aiPlayers[owner] && !(s.mission.mode && owner === "REDCAT")) return { pieceId: null, completedTurn: true, message: "" };
  const recoveryIds = new Set(energyRecoveryPlan(s, owner).map(piece => piece.id));
  const priority = (piece: GamePiece) => {
    if (s.mission.mode && owner === "REDCAT") return piece.type === "FIGHTER" ? -100 : piece.type === "BOMBER" ? -90 : 0;
    if (recoveryIds.has(piece.id)) return -95;
    if (piece.type === "MCV") return -80;
    if (isBuilding(piece.type) && PIECES[piece.type].targets.length) return -65;
    if (piece.type === "BOMBER") return -55;
    if (piece.type === "RAK_INFANTRY" || piece.type === "MG_INFANTRY") return -45;
    if (piece.type === "HARVESTER") return -35;
    if (piece.type === "ING_INFANTRY") return -20;
    if (isTransport(piece.type)) return 10;
    return 0;
  };
  const piece = s.pieces.filter(candidate => candidate.owner === owner && !candidate.activated && (!isBuilding(candidate.type) || PIECES[candidate.type].targets.length > 0 || recoveryIds.has(candidate.id) || (candidate.type === "UPLINK" && s.players[owner].orbitalCooldown === 0))).sort((a, b) => priority(a) - priority(b) || b.id - a.id)[0];
  if (piece) {
    const from: Cell = [piece.col, piece.row], id = piece.id;
    activate(s, piece, retaliationDecision);
    const surviving = s.pieces.find(candidate => candidate.id === id);
    return { pieceId: id, from, to: surviving ? [surviving.col, surviving.row] : from, completedTurn: false, message: s.event };
  }
  if ((s.phase as GameState["phase"]) !== "VICTORY") {
    if (!s.mission.mode && s.activePlayers[owner]) production(s);
    if ((s.phase as GameState["phase"]) !== "VICTORY" && s.currentFaction === owner) endTurn(s);
  }
  checkVictory(s);
  return { pieceId: null, completedTurn: true, message: s.mission.triggerReport || `${owner} AI completed its turn.` };
}
